import { useState, useCallback } from "react";
import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import { logger } from "../../observability/LoggerFactory";
import { useFileDownload } from "../../ui/useFileDownload";

// Единая форма тела ошибки бэкенда: обработчики читают из него текст сообщения.
interface ErrorBody {
  // Отсутствует, когда бэкенд ответил ошибкой без разобранного тела: сообщение
  // тогда собирается из кода ответа.
  error?: string;
}

export type ActionState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string };

export interface UseCompetitorLinkRemovalActionsResult {
  confirmState: ActionState;
  cancelState: ActionState;
  exportState: ActionState;
  confirmRemoval: () => void;
  cancelRemoval: () => void;
  exportResults: () => void;
}

// User-facing error copy is fixed Russian (frontend-language / frontend.interface-copy):
// each message states what failed and what to do next. Raw technical detail
// (`HTTP <status>`, backend error strings, thrown errors) is routed to the logger
// only and never rendered — mirrors the start hook's pattern.
const CONFIRM_ERROR_MESSAGE = "Не удалось подтвердить удаление. Попробуйте ещё раз.";
const CANCEL_ERROR_MESSAGE = "Не удалось отменить прогон. Попробуйте ещё раз.";
const EXPORT_ERROR_MESSAGE = "Не удалось выгрузить результаты. Попробуйте ещё раз.";

// Diagnostic-only: builds a technical detail string for the logger, not for the UI.
async function readErrorDetail(response: Response): Promise<string> {
  let detail = `HTTP ${response.status}`;
  try {
    const body = (await response.json()) as ErrorBody;
    if (typeof body.error === "string") {
      detail = `HTTP ${response.status}: ${body.error}`;
    }
  } catch (error: unknown) {
    // Тело ответа не JSON: сообщение остаётся собранным из кода состояния.
    logger.warn({ err: error, component: "useCompetitorLinkRemovalActions" }, "Error response body is not JSON");
  }
  return detail;
}

export function useCompetitorLinkRemovalActions(
  removalId: string | undefined,
): UseCompetitorLinkRemovalActionsResult {
  const downloadFile = useFileDownload();
  const [confirmState, setConfirmState] = useState<ActionState>({ kind: "idle" });
  const [cancelState, setCancelState] = useState<ActionState>({ kind: "idle" });
  const [exportState, setExportState] = useState<ActionState>({ kind: "idle" });

  const confirmRemoval = useCallback(() => {
    void (async () => {
      if (removalId === undefined) {
        return;
      }
      setConfirmState({ kind: "loading" });
      try {
        const response = await fetchWithRefresh(`/api/competitor-link-removals/${removalId}/confirm`, {
          method: "POST",
        });
        if (!response.ok) {
          throw new Error(await readErrorDetail(response));
        }
        setConfirmState({ kind: "idle" });
      } catch (error: unknown) {
        const detail = error instanceof Error ? error.message : String(error);
        logger.error({ err: detail }, "Failed to confirm competitor link removal");
        setConfirmState({ kind: "error", message: CONFIRM_ERROR_MESSAGE });
      }
    })();
  }, [removalId]);

  const cancelRemoval = useCallback(() => {
    void (async () => {
      if (removalId === undefined) {
        return;
      }
      setCancelState({ kind: "loading" });
      try {
        const response = await fetchWithRefresh(`/api/competitor-link-removals/${removalId}/cancel`, {
          method: "POST",
        });
        if (!response.ok) {
          throw new Error(await readErrorDetail(response));
        }
        setCancelState({ kind: "idle" });
      } catch (error: unknown) {
        const detail = error instanceof Error ? error.message : String(error);
        logger.error({ err: detail }, "Failed to cancel competitor link removal");
        setCancelState({ kind: "error", message: CANCEL_ERROR_MESSAGE });
      }
    })();
  }, [removalId]);

  const exportResults = useCallback(() => {
    void (async () => {
      if (removalId === undefined) {
        return;
      }
      setExportState({ kind: "loading" });
      try {
        const response = await fetchWithRefresh(`/api/competitor-link-removals/${removalId}/results`);
        if (!response.ok) {
          throw new Error(await readErrorDetail(response));
        }
        const blob = await response.blob();
        const contentDisposition = response.headers.get("Content-Disposition");
        const fileNameMatch = contentDisposition ? contentDisposition.match(/filename="(.+)"/) : null;
        const fileName = fileNameMatch ? fileNameMatch[1] : "competitor-link-removal.csv";

        downloadFile(blob, fileName);

        setExportState({ kind: "idle" });
      } catch (error: unknown) {
        const detail = error instanceof Error ? error.message : String(error);
        logger.error({ err: detail }, "Failed to export competitor link removal results");
        setExportState({ kind: "error", message: EXPORT_ERROR_MESSAGE });
      }
    })();
  }, [removalId]);

  return { confirmState, cancelState, exportState, confirmRemoval, cancelRemoval, exportResults };
}
